import { useCallback, useEffect, useMemo } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import queryString from 'query-string';
import ITEMS_PER_PAGE_OPTIONS from '_constants/old/_constants/ITEMS_PER_PAGE_OPTIONS';

const PER_PAGE_VALUES = ITEMS_PER_PAGE_OPTIONS.map((option) => option.value);
const DEFAULT_PER_PAGE = ITEMS_PER_PAGE_OPTIONS[1].value;

// A repeated `?page=1&page=2` parses to an array; take the first and let the validation below judge it.
const firstOf = (raw) => (Array.isArray(raw) ? raw[0] : raw);

const parsePage = (raw) => {
  const value = Number(firstOf(raw));
  return Number.isInteger(value) && value > 0 ? value : 1;
};

// Anything outside the rows-per-page menu — a hand-edited `?page_size=9999`, a stale link — falls back
// to the default rather than being sent to an endpoint that would reject it (max_page_size).
const parsePerPage = (raw, fallback) => {
  const value = Number(firstOf(raw));
  return PER_PAGE_VALUES.includes(value) ? value : fallback;
};

/** The two params a given prefix owns, for callers that need to keep them out of a filter payload. */
export const paginationParamKeys = (prefix = '') => [`${prefix}page`, `${prefix}page_size`];

/**
 * Table pagination mirrored into a pair of query params, so a refresh — or a shared link — puts the
 * user back on the page they were reading instead of snapping to the first one. Same approach the list
 * pages already use for `?activeTab=` and `?tags=` (see useUrlParamState): read on mount, write with
 * history.replace so no history entry is added, leave every other param untouched.
 *
 *   const { page, perPage, handlePageChange, handlePerPageChange, resetPage } = usePaginationParams();
 *
 * `prefix` namespaces the pair, which is what lets two tables on ONE url paginate independently: the
 * Retailers/Visits media galleries take `media_`, so their `media_page` can never be read as — or
 * clobbered by — the List/Map table's own `page`. A tab simply ignores the keys it doesn't own.
 *
 * The value lives in the URL only, never mirrored into state, so there is no second copy to keep in
 * sync. By default each param is dropped while it holds its default, keeping the common case out of the
 * URL; `alwaysInUrl` keeps both written instead. That matters where a second table's params sit on the
 * same URL — on the Media tab, an omitted `media_page_size` next to the List tab's `page_size=50` reads
 * as if the two shared one value and the footer had drifted out of sync with it. Pass it only while the
 * table is the one on screen, so a mounted-but-hidden panel doesn't write its params over someone
 * else's view.
 */
const usePaginationParams = ({ prefix = '', defaultPerPage = DEFAULT_PER_PAGE, alwaysInUrl = false } = {}) => {
  const history = useHistory();
  const location = useLocation();
  const [pageKey, perPageKey] = useMemo(() => paginationParamKeys(prefix), [prefix]);

  const params = useMemo(() => queryString.parse(location.search), [location.search]);
  const page = parsePage(params[pageKey]);
  const perPage = parsePerPage(params[perPageKey], defaultPerPage);

  // Both keys are written in ONE history.replace: a rows-per-page change moves both (new size, back to
  // page 1), and two separate writes in the same tick would each start from the pre-change search
  // string — the second silently reverting the first.
  const set = useCallback(
    (next) => {
      // history.location rather than this render's `location`: replace() updates it synchronously, so
      // consecutive writes compose instead of clobbering each other.
      const { pathname, search } = history.location;
      const current = queryString.parse(search);
      const nextParams = { ...current };

      const write = (key, value, fallback) => {
        if (value === undefined) return;
        if (value === fallback && !alwaysInUrl) delete nextParams[key];
        else nextParams[key] = String(value);
      };

      write(pageKey, next.page, 1);
      write(perPageKey, next.perPage, defaultPerPage);

      const nextSearch = queryString.stringify(nextParams);
      // Re-selecting the current page/size writes nothing, so it can't re-render or refetch.
      if (nextSearch === queryString.stringify(current)) return;
      history.replace({ pathname, search: nextSearch });
    },
    [history, pageKey, perPageKey, defaultPerPage, alwaysInUrl],
  );

  // Seed the URL with the values the table is actually showing, so it states this table's position from
  // the moment it opens rather than only after the user paginates.
  //
  // Keyed on `params`, not on page/perPage: a list fetch on the same page rewrites the whole search
  // string (SetQueryParams) and drops these keys, and it usually does so without changing what this
  // table is showing — so watching the values would miss exactly the case this has to repair. `set`
  // writes nothing when the search string would come out unchanged, so this settles in one replace and
  // cannot loop.
  useEffect(() => {
    if (alwaysInUrl) set({ page, perPage });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [alwaysInUrl, set, params]);

  // `{ value }` — the shape TableSizeControls and InlinePager already call their handlers with.
  const handlePageChange = useCallback(({ value }) => set({ page: value }), [set]);
  // A different page size renumbers every page, so the page the user was on no longer means anything.
  const handlePerPageChange = useCallback(({ value }) => set({ page: 1, perPage: value }), [set]);
  const resetPage = useCallback(() => set({ page: 1 }), [set]);

  return { page, perPage, handlePageChange, handlePerPageChange, resetPage };
};

export default usePaginationParams;
